import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Clock, IdCard, ArrowLeft, Loader2, Calendar, Star } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const tierBadge = {
  decouverte: { label: 'Découverte', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  essentielle: { label: 'Essentielle', cls: 'bg-orange-100 text-orange-700 border-orange-300' },
  prestige: { label: 'Prestige', cls: 'bg-slate-900 text-white border-slate-900' },
};

const statusMeta = {
  active: { label: 'Carte valide', Icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', ring: 'ring-green-200' },
  pending: { label: 'En attente de paiement', Icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', ring: 'ring-amber-200' },
  expired: { label: 'Carte expirée', Icon: XCircle, color: 'text-slate-500', bg: 'bg-slate-50', ring: 'ring-slate-200' },
  cancelled: { label: 'Carte annulée', Icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', ring: 'ring-red-200' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }); }
  catch { return iso.slice(0, 10); }
};

const VerifyMemberPage = () => {
  const { number } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/members/verify/${encodeURIComponent(number)}`);
        if (!cancelled) setState({ loading: false, error: null, data });
      } catch (err) {
        if (!cancelled) setState({
          loading: false,
          error: err.response?.status === 404 ? 'Cette carte est inconnue de notre base.' : 'Impossible de vérifier cette carte pour le moment.',
          data: null,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [number]);

  const meta = state.data ? (statusMeta[state.data.status] || statusMeta.pending) : null;
  const tier = state.data ? (tierBadge[state.data.tier] || tierBadge.decouverte) : null;
  const StatusIcon = meta?.Icon;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-28 pb-20 min-h-[70vh]">
        <section className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold uppercase tracking-widest mb-4">
              <IdCard className="w-3.5 h-3.5" />
              Vérification carte de membre
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" data-testid="verify-title">
              Pierre &amp; Edois
            </h1>
            <p className="text-slate-500 mt-1 text-sm">Numéro scanné&nbsp;: <span className="font-mono text-slate-800">{number}</span></p>
          </div>

          {state.loading && (
            <div className="bg-white border border-slate-200 rounded-2xl p-10 flex flex-col items-center gap-3 shadow-sm" data-testid="verify-loading">
              <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
              <div className="text-slate-500 text-sm">Vérification en cours…</div>
            </div>
          )}

          {!state.loading && state.error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center" data-testid="verify-error">
              <XCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <div className="text-lg font-bold text-red-800 mb-1">Carte introuvable</div>
              <div className="text-sm text-red-700">{state.error}</div>
            </div>
          )}

          {!state.loading && state.data && (
            <div className={`bg-white border-2 rounded-2xl p-8 shadow-lg ring-4 ${meta.ring}`} data-testid="verify-card">
              <div className={`w-16 h-16 rounded-full ${meta.bg} flex items-center justify-center mx-auto mb-4`}>
                <StatusIcon className={`w-9 h-9 ${meta.color}`} />
              </div>
              <div className={`text-center text-lg font-bold ${meta.color} mb-6`} data-testid="verify-status">
                {meta.label}
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Membre</span>
                  <span className="text-slate-900 font-semibold" data-testid="verify-name">
                    {state.data.first_name} {state.data.last_initial}
                  </span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Palier</span>
                  <span className={`px-3 py-1 rounded-full border text-xs font-bold ${tier.cls}`} data-testid="verify-tier">
                    <Star className="inline w-3 h-3 mr-1 -mt-0.5" />
                    {tier.label}
                  </span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Numéro</span>
                  <span className="text-slate-900 font-mono font-bold">{state.data.member_number}</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    Valide jusqu'au
                  </span>
                  <span className="text-slate-900 font-semibold" data-testid="verify-expires">{formatDate(state.data.expires_at)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="text-center mt-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold transition-colors"
              data-testid="verify-home"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour à l'accueil
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default VerifyMemberPage;
